import { CommonModule } from '@angular/common';
import {
	Component,
	DestroyRef,
	Signal,
	computed,
	HostListener,
	inject,
	OnInit,
	signal,
	ViewEncapsulation,
	input,
} from '@angular/core';
import { filter } from 'rxjs';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { VideoLayout } from '../shared/video-layout.service';
import { VbLxpHeaderComponent } from '../header/vb-lxp-header.component';
import { VbLxpFooterComponent } from '../footer/vb-lxp-footer.component';
import { VbLxpThemeToggleComponent } from '../theme/vb-lxp-theme-toggle.component';
import { VbLxpLayoutSelectorComponent } from '../layout/vb-lxp-layout-selector.component';
import { VbLxpSwitchComponent } from '../controls/switch/vb-lxp-switch.component';
import { RaceClassComponent } from '../shared/race-class.component';
import { VbLxpRaceFlagIndicatorComponent } from '../shared/vb-lxp-race-flag-indicator.component';
import {
	VbLxpNotificationComponent,
	Notification,
} from '../notification/vb-lxp-notification.component';

import { ThemeService } from '../shared/theme.service';
import { VideoLayoutService } from '../shared/video-layout.service';
import { PanelService } from '../panel/panel.service';
import { WindowSizeService } from '../shared/window-size.service';
import { RaceSessionService } from '../timing-tower/race-session.service';
import { FeatureSettingsService } from '../shared/feature-settings.service';
import { LivestreamViewService } from '../livestream-view/livestream-view.service';
import { WebSocketService } from '../shared/websocket.service';
import { ModalService } from './modal.service';
import { TimingTowerService } from '../timing-tower/timing-tower.service';
import { VbLxpDropdownComponent } from '../controls/dropdown/vb-lxp-dropdown.component';
import { VbLxpDropdownTriggerForDirective } from '../controls/dropdown/dropdown-trigger-for.directive';

const LAYOUT_SVG_MAP: Record<VideoLayout, string> = {
	[VideoLayout.SINGLE]: 'assets/svg/1x1.svg',
	[VideoLayout.SIDE_BY_SIDE]: 'assets/svg/1x2.svg',
	[VideoLayout.LARGE_LEFT]: 'assets/svg/lg-left.svg',
	[VideoLayout.LARGE_RIGHT]: 'assets/svg/lg-right.svg',
	[VideoLayout.GRID_2X2]: 'assets/svg/2x2.svg',
	[VideoLayout.STACKED]: 'assets/svg/2x1.svg',
};

export interface HeaderConfig {
	accentBar?: boolean;
	centerImageSrcLight?: string;
	centerImageSrcDark?: string;
	centerImageAlt?: string;
	centerText?: string;
	logoLink?: string;
	logoLinkTarget?: string;
	hideFlagIndicator?: boolean;
	raceTitle?: string;
	raceDetail?: string;
}

export interface FooterConfigItem {
	srcLight: string;
	srcDark: string;
	link: string;
}

@Component({
	selector: 'vb-lxp-root',
	imports: [
		CommonModule,
		RouterModule,
		VbLxpHeaderComponent,
		VbLxpDropdownComponent,
		VbLxpDropdownTriggerForDirective,
		VbLxpFooterComponent,
		VbLxpThemeToggleComponent,
		VbLxpLayoutSelectorComponent,
		VbLxpSwitchComponent,
		RaceClassComponent,
		VbLxpRaceFlagIndicatorComponent,
		VbLxpNotificationComponent,
	],
	templateUrl: './vb-lxp.component.html',
	styleUrl: './vb-lxp.component.css',
	encapsulation: ViewEncapsulation.None,
	host: {
		'(document:click)': 'onDocumentClick($event)',
	},
})
export class VbLxp implements OnInit {
	public themeService = inject(ThemeService);
	public layoutService = inject(VideoLayoutService);
	public panelService = inject(PanelService);
	public windowSizeService = inject(WindowSizeService);
	public raceSessionService = inject(RaceSessionService);
	public featureSettingsService = inject(FeatureSettingsService);
	public livestreamViewService = inject(LivestreamViewService);
	public timingTowerService = inject(TimingTowerService);
	public webSocketService = inject(WebSocketService);
	public modalService = inject(ModalService);
	public router = inject(Router);
	private readonly destroyRef = inject(DestroyRef);

	private featureSettings = toSignal(this.featureSettingsService.featureSettings$);
	public isDriverSelectDropdownOpen = signal(false);
	public supportStreams = toSignal(this.webSocketService.supportStream$);

	public headerConfig = input<HeaderConfig>();
	public footerConfig = input<FooterConfigItem[]>();

	protected readonly VideoLayout = VideoLayout;
	protected readonly layoutSvgMap = LAYOUT_SVG_MAP;
	protected activeLayoutSvg = '';
	protected readonly footerImages = computed<{ src: string; link: string }[]>(() => {
		return this.footerConfig().map((c) => {
			const logoSrc = this.themeService.isDarkMode() ? c.srcDark : c.srcLight;
			return { src: logoSrc, link: c.link };
		});
	});

	isDropdownOpen = signal(false);
	isMobile = signal(false);
	isTimingActive = toSignal(this.panelService.getPanelState('timing'));
	isMapActive = toSignal(this.panelService.getPanelState('map'));
	isChatActive = toSignal(this.panelService.getPanelState('chat'));
	public isSessionArchived = toSignal(this.raceSessionService.isSessionArchived$);
	public raceInfo = toSignal(this.raceSessionService.raceInfo$);
	public raceStatus = toSignal(this.raceSessionService.raceStatus$);
	public timeStatus = toSignal(this.raceSessionService.timeStatus$);
	public raceClasses = toSignal(this.raceSessionService.raceClasses$, { initialValue: [] });
	public navigationEnd = toSignal(
		this.router.events.pipe(filter((event) => event instanceof NavigationEnd)),
	);

	public currentNotification = computed<Notification | null>(() => {
		const msg = this.featureSettings()?.notificationMessage?.trim();
		return msg ? { type: 'info', message: msg } : null;
	});

	public isAdminRoute = computed(() => {
		this.navigationEnd();
		return this.router.url.startsWith('/admin') || this.router.url.startsWith('/login');
	});

	public controlVisibilities: Signal<any> = computed(() => {
		this.navigationEnd();
		let route = this.router.routerState.root;

		while (route.firstChild) {
			route = route.firstChild;
		}

		return route.snapshot.data;
	});

	private readonly MOBILE_BREAKPOINT = 768;
	private desktopLayout: VideoLayout | null = null;

	ngOnInit() {
		// Initialize SSE connection for public project (no auth required)
		this.webSocketService.connect();
		this.modalService.registerModal('race-control');

		this.checkScreenSize();
		this.layoutService.activeLayout$
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((activeLayout) => {
				this.activeLayoutSvg = LAYOUT_SVG_MAP[activeLayout];
				this.desktopLayout = activeLayout;
			});
	}

	@HostListener('window:resize')
	onResize() {
		this.checkScreenSize();
	}

	private checkScreenSize() {
		const wasMobile = this.isMobile();
		const nowMobile = window.innerWidth < this.MOBILE_BREAKPOINT;
		this.isMobile.set(nowMobile);
		this.windowSizeService.updateWindowSize();

		if (nowMobile && !wasMobile) {
			// Entering mobile: save current layout and force single
			this.layoutService.setLayout(VideoLayout.SINGLE);
		} else if (!nowMobile && wasMobile && this.desktopLayout !== null) {
			// Leaving mobile: restore previous layout
			this.layoutService.setLayout(this.desktopLayout);
			this.desktopLayout = null;
		}
	}

	toggleDropdown(event: Event) {
		event.stopPropagation();
		this.isDropdownOpen.update((open) => !open);
	}

	public openStreamPage() {
		const currentQueryParams = window.location.search;
		const newRoute = '/streams';
		const fullUrl = `${newRoute}${currentQueryParams}`;

		// navigate to full url
		this.router.navigateByUrl(fullUrl);
	}

	onDocumentClick(_event: Event) {
		this.isDropdownOpen.set(false);
		this.isDriverSelectDropdownOpen.set(false);
	}

	onThemeToggle() {
		this.themeService.toggleDarkMode();
	}

	toggleTiming() {
		this.panelService.togglePanel('timing');
	}

	toggleChat() {
		this.panelService.togglePanel('chat');
	}

	toggleMap() {
		this.panelService.togglePanel('map');
	}

	onClassSelectionChange(selectedClasses: string[]) {
		this.raceSessionService.updateSelectedClasses(selectedClasses);
	}

	public toggleDriverSelect(event) {
		event.stopPropagation();
		this.isDriverSelectDropdownOpen.update((s) => !s);
	}

	public onSpa24DriverSelected(event, id: string, teamName: string, url: string) {
		event.stopPropagation();
		if (!this.timingTowerService.activeList().includes(id)) {
			this.onSpa24StreamAdded(id, teamName, url);
		} else {
			this.onSpa24StreamRemoved(id, teamName, url);
		}
	}

	public onSpa24StreamAdded(id: string, teamName: string, url: string) {
		this.timingTowerService.setActive(id);
		const raceStreamData = {
			isLive: true,
			carNumber: id,
			driverName: '',
			hlsPlaybackUrl: url,
			teamName: teamName,
		};
		this.livestreamViewService.addSlotItem({
			type: 'timing',
			id: id,
			streamData: raceStreamData,
			timestamp: Date.now(),
		});
	}

	public onSpa24StreamRemoved(id: string, teamName: string, url: string) {
		this.timingTowerService.setInactive(id);
		const raceStreamData = {
			isLive: true,
			carNumber: id,
			driverName: '',
			hlsPlaybackUrl: url,
			teamName: teamName,
		};
		this.livestreamViewService.removeSlotItem({
			type: 'timing',
			id: id,
			streamData: raceStreamData,
			timestamp: Date.now(),
		});
	}
}
